/**
 * ChatHistorySidebarBase — Shared base component for chat history sidebars.
 *
 * Used by both Assistant ChatHistorySidebar and TenderChat TenderChatHistorySidebar.
 * Handles: collapsible layout, resizable width, date grouping, rename dialog, dropdown menus.
 */

import { useState, useEffect, useCallback } from "react"
import { Plus, MessageSquare, MoreHorizontal, SquarePen, Trash, PanelRight, Loader2, Pin, PinOff } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const MIN_WIDTH = 256
const MAX_WIDTH = 512

// ===================
// Types
// ===================

export interface SidebarSession {
  id: string
  title?: string | null
  updatedAt: string
  userId: string
  organizationId: string
  bidId?: string | null
  createdAt: string
  messageCount: number
  lastMessageAt: string | null
  isPinned?: boolean
}

interface GroupedSessions {
  today: SidebarSession[]
  yesterday: SidebarSession[]
  previous7Days: SidebarSession[]
  older: SidebarSession[]
}

export interface ChatHistorySidebarBaseProps {
  isExpanded: boolean
  onExpandedChange: (expanded: boolean) => void
  sessions: SidebarSession[]
  currentSessionId: string | null | undefined
  onNewChat: () => void
  onSelectSession: (sessionId: string) => void
  onDeleteSession?: (sessionId: string) => Promise<void>
  onRenameSession?: (sessionId: string, newTitle: string) => Promise<void>
  onTogglePin?: (sessionId: string) => Promise<void> | void
  headerTitle: string
  emptyText: string
  emptyActionText: string
  /**
   * The list has not loaded yet. With no rows, shows a spinner instead of the
   * empty state, so "No conversations yet" only ever follows a real empty
   * result. Ignored once rows exist.
   */
  isLoading?: boolean
  renameDialogTitle?: string
  renameDialogDescription?: string
  renameInputLabel?: string
  renameInputPlaceholder?: string
  /**
   * Per-item rename/pin/delete affordances. Defaults to true — both the
   * assistant and tender-chat surfaces use the default. Pass false to render a
   * read-only history list (no row dropdown, no rename/delete dialogs).
   */
  showItemActions?: boolean
  /**
   * Fill the host instead of being a resizable right-hand rail: no border,
   * resize handle, collapse toggle or header new-chat button. The assistant
   * dock shows history this way, in place of the conversation.
   */
  fill?: boolean
}

// ===================
// Helpers
// ===================

function groupSessionsByDate(sessions: SidebarSession[]): GroupedSessions {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)

  const groups: GroupedSessions = {
    today: [],
    yesterday: [],
    previous7Days: [],
    older: [],
  }

  sessions.forEach((session) => {
    const date = new Date(session.updatedAt)
    if (date >= today) {
      groups.today.push(session)
    } else if (date >= yesterday) {
      groups.yesterday.push(session)
    } else if (date >= lastWeek) {
      groups.previous7Days.push(session)
    } else {
      groups.older.push(session)
    }
  })

  return groups
}

// ===================
// Main Component
// ===================

export function ChatHistorySidebarBase({
  isExpanded,
  onExpandedChange,
  sessions,
  currentSessionId,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  onRenameSession,
  onTogglePin,
  headerTitle,
  emptyText,
  emptyActionText,
  isLoading = false,
  renameDialogTitle,
  renameDialogDescription,
  renameInputLabel,
  renameInputPlaceholder,
  showItemActions = true,
  fill = false,
}: ChatHistorySidebarBaseProps) {
  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renamingSession, setRenamingSession] = useState<SidebarSession | null>(null)
  const [newTitle, setNewTitle] = useState("")
  const [isRenaming, setIsRenaming] = useState(false)

  // Delete confirmation state
  const [deletingSession, setDeletingSession] = useState<SidebarSession | null>(null)

  // Resizable width state
  const [width, setWidth] = useState(MIN_WIDTH)
  const [isResizing, setIsResizing] = useState(false)

  const handleOpenRenameDialog = (session: SidebarSession) => {
    setRenamingSession(session)
    setNewTitle(session.title ?? "")
    setRenameDialogOpen(true)
  }

  const handleRename = async () => {
    if (!renamingSession || !newTitle.trim() || newTitle === renamingSession.title || !onRenameSession) return

    setIsRenaming(true)
    try {
      await onRenameSession(renamingSession.id, newTitle.trim())
      setRenameDialogOpen(false)
    } catch {
      // Error handled in store
    } finally {
      setIsRenaming(false)
    }
  }

  useEffect(() => {
    if (renameDialogOpen && renamingSession) {
      setNewTitle(renamingSession.title ?? "")
    }
  }, [renameDialogOpen, renamingSession])

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const containerRight = window.innerWidth
      const newWidth = containerRight - e.clientX
      setWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth)))
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isResizing])

  // Pinned threads live in their own group above the date groups, newest first,
  // and are excluded from the date grouping so each thread renders exactly once.
  const pinnedSessions = sessions
    .filter((s) => s.isPinned)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  const groupedSessions = groupSessionsByDate(sessions.filter((s) => !s.isPinned))

  const renderSessionGroup = (title: string, groupSessions: SidebarSession[]) => {
    if (groupSessions.length === 0) return null

    return (
      <div className="mb-4">
        <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">{title}</div>
        <div className="space-y-0.5">
          {groupSessions.map((session) => (
            <div
              key={session.id}
              className={cn(
                "group relative flex items-center gap-2 pr-1 rounded-md cursor-pointer transition-colors",
                currentSessionId === session.id
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              {/* A real button, not a clickable div, so the thread is
                  keyboard-operable and announced. The row-actions menu is a
                  sibling rather than a child to avoid nesting interactives. */}
              <button
                type="button"
                className="flex-1 min-w-0 py-2 pl-3 text-left"
                aria-current={currentSessionId === session.id ? "true" : undefined}
                onClick={() => onSelectSession(session.id)}
              >
                <span className="text-sm truncate block">
                  {session.title?.trim() ? session.title : "New Chat"}
                </span>
              </button>
              {showItemActions && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Conversation actions"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onTogglePin && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          void onTogglePin(session.id)
                        }}
                      >
                        {session.isPinned ? (
                          <>
                            <PinOff className="mr-2 h-4 w-4" />
                            Unpin
                          </>
                        ) : (
                          <>
                            <Pin className="mr-2 h-4 w-4" />
                            Pin
                          </>
                        )}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenRenameDialog(session)
                      }}
                    >
                      <SquarePen className="mr-2 h-4 w-4" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeletingSession(session)
                      }}
                    >
                      <Trash className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
    <div
      className={cn(
        "relative flex flex-col h-full",
        fill ? "w-full" : "border-l border-border",
        !fill && !isExpanded && "w-12",
        isResizing && "select-none"
      )}
      style={isExpanded && !fill ? { width: `${width}px` } : undefined}
    >
      {/* Resize Handle */}
      {isExpanded && !fill && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-primary/20 active:bg-primary/30 z-10"
          onMouseDown={handleMouseDown}
        />
      )}
      {/* Header */}
      <div className={cn(
        "h-12 flex items-center px-3",
        isExpanded ? "justify-between" : "justify-center"
      )}>
        {isExpanded ? (
          <>
            <span className="text-sm font-medium">{headerTitle}</span>
            {!fill && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={onNewChat}
              aria-label="New conversation"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
            )}
          </>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={onNewChat}
            aria-label="New conversation"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Sessions List */}
      {isExpanded && (
        <ScrollArea className="flex-1 py-2 scrollbar-thin">
          {renderSessionGroup("Pinned", pinnedSessions)}
          {renderSessionGroup("Today", groupedSessions.today)}
          {renderSessionGroup("Yesterday", groupedSessions.yesterday)}
          {renderSessionGroup("Previous 7 Days", groupedSessions.previous7Days)}
          {renderSessionGroup("Older", groupedSessions.older)}

          {sessions.length === 0 && isLoading && (
            <div
              role="status"
              aria-label="Loading conversations"
              className="flex items-center justify-center py-8"
            >
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
            </div>
          )}

          {sessions.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <MessageSquare className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">{emptyText}</p>
              <Button
                variant="link"
                size="sm"
                className="mt-1"
                onClick={onNewChat}
              >
                {emptyActionText}
              </Button>
            </div>
          )}
        </ScrollArea>
      )}

      {/* Toggle Button at Bottom */}
      {!fill && (
      <div className="p-2 flex items-center justify-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onExpandedChange(!isExpanded)}
          className="h-8 w-8 text-muted-foreground hover:bg-accent hover:text-foreground transition-all duration-200"
          aria-label={isExpanded ? "Collapse conversations" : "Expand conversations"}
          aria-expanded={isExpanded}
        >
          <PanelRight className="h-4 w-4" />
        </Button>
      </div>
      )}
    </div>

    {/* Rename Dialog */}
    {showItemActions && (
    <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{renameDialogTitle}</DialogTitle>
          <DialogDescription>
            {renameDialogDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-2">
            <Label htmlFor="sidebar-rename-title">{renameInputLabel}</Label>
            <Input
              id="sidebar-rename-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newTitle.trim() && newTitle !== renamingSession?.title) {
                  void handleRename()
                }
              }}
              autoFocus
              disabled={isRenaming}
              placeholder={renameInputPlaceholder}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setRenameDialogOpen(false)}
            disabled={isRenaming}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleRename()}
            disabled={!newTitle.trim() || newTitle === renamingSession?.title || isRenaming}
          >
            {isRenaming && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    )}

    {/* Delete Confirmation */}
    {showItemActions && (
    <AlertDialog
      open={deletingSession !== null}
      onOpenChange={(open) => {
        if (!open) setDeletingSession(null)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete this conversation and its messages. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              if (deletingSession) {
                // The dialog closes on confirm, so the store's rethrown rejection
                // has no UI to land in — keep it observed here. The store's error
                // state carries the user-facing message.
                void Promise.resolve(onDeleteSession?.(deletingSession.id)).catch(() => {})
              }
              setDeletingSession(null)
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    )}
    </>
  )
}
